import { useCallback, useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { CheckCircle, Loader2, Search, Trash2, Upload, X, XCircle } from "lucide-react";
import {
  useTenantUsers,
  useRemoveTenantUser,
  useTenantUserDevices,
  useAssignDevice,
  useUnassignDevice,
  useImportTenantUsers,
} from "@/api/tenant-users";
import type { ImportUsersResult } from "@/api/tenant-users";
import { useTenantInvitations, useCreateInvitation, useRevokeInvitation } from "@/api/invitations";
import { useTenantDevices } from "@/api/devices";
import { useDebounce } from "@/hooks/use-debounce";
import { usePagination } from "@/hooks/use-pagination";
import { useTenantContext } from "@/hooks/use-tenant-context";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/data-table/data-table";
import { Dialog, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAuth } from "@/stores/auth-store";
import { canManageTenantResources } from "@/lib/roles";
import { AddUserDialog } from "@/components/add-user-dialog";
import type { TenantUser, TenantUserDevice, Device, Invitation } from "@/api/types";
import type { AxiosError } from "axios";

const roleColors: Record<string, "danger" | "accent" | "warning" | "info" | "default"> = {
  super_admin: "danger",
  operator: "accent",
  client_admin: "warning",
  client_viewer: "info",
  tenant_member: "default",
};

export function TenantUsersPage() {
  const { user } = useAuth();
  const { tenantId, tenant } = useTenantContext();
  const { page, pageSize, search, filters, params, setPage, setPageSize, setSearch, setFilter } = usePagination();
  const { data, isLoading } = useTenantUsers(tenantId, params);
  const roleFilter = filters["filter[role]"] || "";
  const [addOpen, setAddOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [manageUser, setManageUser] = useState<TenantUser | null>(null);
  const [devicesUser, setDevicesUser] = useState<TenantUser | null>(null);
  const [removeTarget, setRemoveTarget] = useState<TenantUser | null>(null);
  const removeTenantUser = useRemoveTenantUser(tenantId);
  const { data: invitationsData } = useTenantInvitations(tenantId, { "filter[status]": "pending" });
  const revokeInvitation = useRevokeInvitation(tenantId);
  const pendingInvitations = invitationsData?.data ?? [];

  const exportCsv = useCallback(() => {
    const rows = data?.data ?? [];
    if (rows.length === 0) return;
    const headers = ["Email", "Name", "Role", "Status", "Devices", "Created"];
    const csvRows = [
      headers.join(","),
      ...rows.map((u) => {
        const name = [u.first_name, u.last_name].filter(Boolean).join(" ");
        return [
          `"${u.email}"`,
          `"${name}"`,
          u.role,
          u.status,
          u.device_count,
          new Date(u.created_at).toLocaleDateString(),
        ].join(",");
      }),
    ];
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `tenant-users${roleFilter ? `-${roleFilter}` : ""}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [data, roleFilter]);

  const columns: ColumnDef<TenantUser, unknown>[] = [
    { accessorKey: "email", header: "Email" },
    {
      id: "name",
      header: "Name",
      cell: ({ row }) => {
        const { first_name, last_name } = row.original;
        return [first_name, last_name].filter(Boolean).join(" ") || "—";
      },
    },
    {
      accessorKey: "role",
      header: "Role",
      cell: ({ row }) => (
        <Badge variant={roleColors[row.original.role]}>
          {row.original.role.replace("_", " ")}
        </Badge>
      ),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => (
        <Badge variant={row.original.status === "active" ? "success" : "danger"}>
          {row.original.status}
        </Badge>
      ),
    },
    {
      accessorKey: "device_count",
      header: "Devices",
      cell: ({ row }) =>
        row.original.device_count > 0 ? (
          <Button
            variant="ghost"
            size="sm"
            className="font-medium text-accent hover:underline"
            onClick={(e) => {
              e.stopPropagation();
              setDevicesUser(row.original);
            }}
          >
            {row.original.device_count}
          </Button>
        ) : (
          <span className="text-text-muted">0</span>
        ),
    },
    {
      accessorKey: "created_at",
      header: "Created",
      cell: ({ row }) => new Date(row.original.created_at).toLocaleDateString(),
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) =>
        canManageTenantResources(user) ? (
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                setManageUser(row.original);
              }}
            >
              Devices
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-danger hover:text-danger"
              onClick={(e) => {
                e.stopPropagation();
                setRemoveTarget(row.original);
              }}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ) : null,
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">
          {tenant?.name ? `${tenant.name} — Users` : "Users"}
        </h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={exportCsv} disabled={!data?.data?.length}>Export CSV</Button>
          {canManageTenantResources(user) && (
            <>
              <Button variant="outline" onClick={() => setImportOpen(true)}>
                <Upload className="mr-1.5 h-3.5 w-3.5" />
                Import CSV
              </Button>
              <Button variant="outline" onClick={() => setInviteOpen(true)}>Invite User</Button>
              <Button onClick={() => setAddOpen(true)}>Add User</Button>
            </>
          )}
        </div>
      </div>
      {pendingInvitations.length > 0 && (
        <div className="rounded-lg border border-border bg-surface p-4">
          <h3 className="mb-3 text-sm font-medium text-text-primary">Pending Invitations</h3>
          <div className="space-y-2">
            {pendingInvitations.map((inv: Invitation) => (
              <div key={inv.id} className="flex items-center justify-between rounded-lg bg-surface-hover px-3 py-2">
                <div className="flex items-center gap-3">
                  <span className="text-sm text-text-primary">{inv.email}</span>
                  <Badge variant={inv.role === "client_admin" ? "warning" : "info"}>
                    {inv.role.replace("_", " ")}
                  </Badge>
                  <span className="text-xs text-text-muted">
                    Expires {new Date(inv.expires_at).toLocaleDateString()}
                  </span>
                </div>
                {canManageTenantResources(user) && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-danger hover:text-danger"
                    onClick={() => revokeInvitation.mutate(inv.id)}
                    disabled={revokeInvitation.isPending}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <DataTable
        columns={columns}
        data={data?.data ?? []}
        isLoading={isLoading}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search users..."
        toolbar={
          <div className="flex gap-2">
            <Select
              value={roleFilter}
              onChange={(e) => setFilter("role", e.target.value || null)}
              className="w-36"
            >
              <option value="">All Roles</option>
              <option value="super_admin">Super Admin</option>
              <option value="operator">Operator</option>
              <option value="client_admin">Client Admin</option>
              <option value="client_viewer">Client Viewer</option>
              <option value="tenant_member">Tenant Member</option>
            </Select>
            <Select
              value={filters["filter[has_devices]"] || ""}
              onChange={(e) => setFilter("has_devices", e.target.value || null)}
              className="w-40"
            >
              <option value="">All Users</option>
              <option value="true">With Devices</option>
              <option value="false">Without Devices</option>
            </Select>
          </div>
        }
        page={page}
        pageSize={pageSize}
        totalPages={data?.meta?.totalPages ?? 1}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        total={data?.meta?.total}
      />

      {inviteOpen && (
        <InviteUserDialog
          open
          onClose={() => setInviteOpen(false)}
          tenantId={tenantId}
        />
      )}

      {addOpen && (
        <AddUserDialog
          open
          onClose={() => setAddOpen(false)}
          tenantId={tenantId}
        />
      )}

      {importOpen && (
        <ImportUsersDialog
          open
          onClose={() => setImportOpen(false)}
          tenantId={tenantId}
        />
      )}

      {manageUser && (
        <ManageDevicesDialog
          open
          onClose={() => setManageUser(null)}
          tenantId={tenantId}
          user={manageUser}
        />
      )}

      <Dialog open={!!removeTarget} onClose={() => setRemoveTarget(null)}>
        <DialogTitle>Confirm Remove</DialogTitle>
        <p className="text-sm text-text-secondary">
          Are you sure you want to remove{" "}
          <span className="font-medium text-text-primary">
            {removeTarget?.email}
          </span>{" "}
          from this tenant? Their device assignments will also be removed.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={() => setRemoveTarget(null)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => {
              if (removeTarget) {
                removeTenantUser.mutate(removeTarget.id, {
                  onSettled: () => setRemoveTarget(null),
                });
              }
            }}
            disabled={removeTenantUser.isPending}
          >
            {removeTenantUser.isPending ? "Removing..." : "Remove"}
          </Button>
        </div>
      </Dialog>

      {devicesUser && (
        <UserDevicesPopup
          open
          onClose={() => setDevicesUser(null)}
          tenantId={tenantId}
          user={devicesUser}
        />
      )}
    </div>
  );
}

function UserDevicesPopup({
  open,
  onClose,
  tenantId,
  user,
}: {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  user: TenantUser;
}) {
  const { data, isLoading } = useTenantUserDevices(tenantId, user.id);
  const devices = data?.data ?? [];

  return (
    <Dialog open={open} onClose={onClose} className="max-w-xl">
      <DialogTitle>Devices — {user.email}</DialogTitle>
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-text-muted" />
        </div>
      ) : devices.length === 0 ? (
        <p className="py-4 text-sm text-text-muted">No devices assigned.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Hostname</TableHead>
              <TableHead>Serial</TableHead>
              <TableHead>Platform</TableHead>
              <TableHead>Primary</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {devices.map((d: TenantUserDevice) => (
              <TableRow key={d.id}>
                <TableCell className="font-medium">{d.hostname || "—"}</TableCell>
                <TableCell className="font-mono text-text-secondary">{d.serial_number || "—"}</TableCell>
                <TableCell>{d.platform || "—"}</TableCell>
                <TableCell>
                  {d.is_primary && <Badge variant="accent">Primary</Badge>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <div className="mt-4 flex justify-end">
        <Button variant="outline" onClick={onClose}>Close</Button>
      </div>
    </Dialog>
  );
}

function ImportUsersDialog({
  open,
  onClose,
  tenantId,
}: {
  open: boolean;
  onClose: () => void;
  tenantId: string;
}) {
  const importUsers = useImportTenantUsers(tenantId);
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<ImportUsersResult | null>(null);

  const handleUpload = () => {
    if (!file) return;
    importUsers.mutate(file, {
      onSuccess: (data) => setResult(data),
    });
  };

  const downloadTemplate = () => {
    const csv = "email,first_name,last_name,serial_numbers\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "import-users-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleClose = () => {
    setFile(null);
    setResult(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>Import Users</DialogTitle>

      {result ? (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-lg bg-surface-hover p-3 text-center">
              <p className="text-2xl font-bold text-success">{result.created}</p>
              <p className="text-xs text-text-muted">Created</p>
            </div>
            <div className="rounded-lg bg-surface-hover p-3 text-center">
              <p className="text-2xl font-bold text-text-secondary">{result.skipped}</p>
              <p className="text-xs text-text-muted">Skipped</p>
            </div>
            <div className="rounded-lg bg-surface-hover p-3 text-center">
              <p className="text-2xl font-bold text-accent">{result.devices_mapped}</p>
              <p className="text-xs text-text-muted">Devices Mapped</p>
            </div>
          </div>

          {result.errors.length > 0 && (
            <div className="max-h-40 overflow-y-auto rounded-lg border border-danger/30 bg-danger/5 p-3">
              <p className="mb-2 text-xs font-medium text-danger">Errors ({result.errors.length})</p>
              {result.errors.map((err, i) => (
                <div key={i} className="flex items-start gap-1.5 text-xs text-danger/80">
                  <XCircle className="mt-0.5 h-3 w-3 shrink-0" />
                  <span>{err}</span>
                </div>
              ))}
            </div>
          )}

          {result.errors.length === 0 && (
            <div className="flex items-center gap-2 text-sm text-success">
              <CheckCircle className="h-4 w-4" />
              Import completed successfully.
            </div>
          )}

          <div className="flex justify-end">
            <Button onClick={handleClose}>Done</Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-text-secondary">
            Upload a CSV file to import users. All users will be created with the
            <Badge variant="default" className="mx-1">tenant member</Badge>
            role.
          </p>

          <div className="rounded-lg border border-dashed border-border p-4">
            <Input
              type="file"
              accept=".csv"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </div>

          <button
            type="button"
            onClick={downloadTemplate}
            className="text-xs text-accent hover:underline"
          >
            Download CSV template
          </button>

          <p className="text-xs text-text-muted">
            Columns: <code>email</code> (required), <code>first_name</code>, <code>last_name</code>, <code>serial_numbers</code> (comma-separated to auto-assign devices).
            Max 500 rows.
          </p>

          {importUsers.isError && (
            <div className="flex items-start gap-2 text-sm text-danger">
              <XCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                {(importUsers.error as { response?: { data?: { error?: { message?: string } } } })
                  ?.response?.data?.error?.message || "Import failed."}
              </span>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={handleClose}>Cancel</Button>
            <Button onClick={handleUpload} disabled={!file || importUsers.isPending}>
              {importUsers.isPending ? (
                <><Loader2 className="mr-2 h-3 w-3 animate-spin" />Importing...</>
              ) : "Import"}
            </Button>
          </div>
        </div>
      )}
    </Dialog>
  );
}

function InviteUserDialog({
  open,
  onClose,
  tenantId,
}: {
  open: boolean;
  onClose: () => void;
  tenantId: string;
}) {
  const createInvitation = useCreateInvitation(tenantId);
  const [form, setForm] = useState({ email: "", role: "client_viewer" });
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    createInvitation.mutate(form, {
      onSuccess: () => {
        onClose();
        setForm({ email: "", role: "client_viewer" });
      },
      onError: (err) => {
        const axiosErr = err as AxiosError<{ error?: { message?: string } }>;
        setError(axiosErr.response?.data?.error?.message || "Failed to send invitation.");
      },
    });
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Invite User</DialogTitle>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-text-muted">Email</label>
          <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required autoFocus />
        </div>
        <div>
          <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-text-muted">Role</label>
          <Select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
            <option value="client_viewer">Client Viewer</option>
            <option value="client_admin">Client Admin</option>
          </Select>
        </div>
        {error && <p className="text-sm text-danger">{error}</p>}
        <p className="text-xs text-text-muted">An invitation email will be sent with a link to set up their account.</p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={createInvitation.isPending}>
            {createInvitation.isPending ? "Sending..." : "Send Invitation"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function ManageDevicesDialog({
  open,
  onClose,
  tenantId,
  user,
}: {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  user: TenantUser;
}) {
  const { data: devicesData, isLoading } = useTenantUserDevices(tenantId, user.id);
  const assignDevice = useAssignDevice(tenantId, user.id);
  const unassignDevice = useUnassignDevice(tenantId, user.id);
  const [showAddDevice, setShowAddDevice] = useState(false);

  const assignments = devicesData?.data ?? [];

  return (
    <Dialog open={open} onClose={onClose} className="max-w-2xl">
      <DialogTitle>
        Manage Devices — {user.email}
      </DialogTitle>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-text-muted" />
        </div>
      ) : assignments.length === 0 && !showAddDevice ? (
        <p className="py-4 text-sm text-text-muted">No devices assigned to this user.</p>
      ) : (
        assignments.length > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Hostname</TableHead>
                <TableHead>Serial</TableHead>
                <TableHead>Platform</TableHead>
                <TableHead>Primary</TableHead>
                <TableHead className="w-20" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {assignments.map((a: TenantUserDevice) => (
                <TableRow key={a.id}>
                  <TableCell className="font-medium">{a.hostname || "—"}</TableCell>
                  <TableCell className="font-mono text-text-secondary">{a.serial_number || "—"}</TableCell>
                  <TableCell>{a.platform || "—"}</TableCell>
                  <TableCell>
                    {a.is_primary && <Badge variant="accent">Primary</Badge>}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-danger hover:text-danger"
                      onClick={() => unassignDevice.mutate(a.device_id)}
                      disabled={unassignDevice.isPending}
                    >
                      Remove
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )
      )}

      {showAddDevice ? (
        <AddDeviceSection
          tenantId={tenantId}
          userId={user.id}
          existingDeviceIds={assignments.map((a) => a.device_id)}
          onAssigned={() => setShowAddDevice(false)}
          assignDevice={assignDevice}
        />
      ) : (
        <div className="mt-4 flex justify-between">
          <Button size="sm" variant="outline" onClick={() => setShowAddDevice(true)}>
            Assign Device
          </Button>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      )}
    </Dialog>
  );
}

function AddDeviceSection({
  tenantId,
  userId,
  existingDeviceIds,
  onAssigned,
  assignDevice,
}: {
  tenantId: string;
  userId: string;
  existingDeviceIds: string[];
  onAssigned: () => void;
  assignDevice: ReturnType<typeof useAssignDevice>;
}) {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search.trim(), 300);

  const deviceParams = useMemo(() => {
    const p: Record<string, string> = {};
    if (debouncedSearch) p.search = debouncedSearch;
    return p;
  }, [debouncedSearch]);

  const { data, isFetching } = useTenantDevices(tenantId, deviceParams);
  const isDebouncing = search.trim() !== debouncedSearch;

  const available = useMemo(() => {
    const all = data?.data ?? [];
    return all.filter((d: Device) => !existingDeviceIds.includes(d.id));
  }, [data, existingDeviceIds]);

  const handleAssign = (deviceId: string) => {
    assignDevice.mutate({ device_id: deviceId }, { onSuccess: onAssigned });
  };

  return (
    <div className="mt-4 rounded-lg border border-border p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm font-medium text-text-primary">Assign a Device</span>
        <Button size="sm" variant="ghost" onClick={onAssigned}>
          Cancel
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
        <Input
          placeholder="Search devices..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
          autoFocus
        />
        {(isFetching || isDebouncing) && (
          <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-text-muted" />
        )}
      </div>

      <div className="mt-2 max-h-48 space-y-1 overflow-y-auto">
        {available.length === 0 ? (
          <p className="py-4 text-center text-sm text-text-muted">
            {search ? "No matching devices found." : "No devices available to assign."}
          </p>
        ) : (
          available.map((d: Device) => (
            <div
              key={d.id}
              className="flex items-center justify-between rounded-lg px-3 py-2 hover:bg-surface-hover"
            >
              <div className="min-w-0 flex-1">
                <span className="truncate text-sm font-medium">{d.hostname || "—"}</span>
                <p className="mt-0.5 text-xs text-text-muted font-mono">
                  {d.serial_number || "No serial"} · {d.platform || "Unknown"}
                </p>
              </div>
              <Button
                size="sm"
                onClick={() => handleAssign(d.id)}
                disabled={assignDevice.isPending}
                className="ml-3 shrink-0"
              >
                Assign
              </Button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
